import { useEffect, useState } from 'react';
import type { UserBranches, UserRoles } from './models/UserInterfaces';
import { GetQuery } from './Shared';
import { toast } from 'react-toastify';

interface UserRoleProps {
    id: number;
    driverSystem: boolean;
    skipTruckToolCheck: boolean;
    selectedUserRoles: UserRoles[];
    selectedUserBranches: UserBranches[];
    addRole(userRole: UserRoles): void;
    removeRole(id: number): void;
    addBranch(userBranch: UserBranches): void;
    removeBranch(id: number): void;
    updateDriverSystem(driverSystem: boolean, skipTruckToolCheck: boolean): void;
    save(): void;
}

const UserRoleData = (props: UserRoleProps) => {
    const [loading, setLoading] = useState(true);
    const [errors, setErrors] = useState<{ [key: string]: string }> ({});
    const [roleId, setRoleId] = useState(0);
    const [roles, setRoles] = useState<UserRoles[]>([]);
    const [branchId, setBranchId] = useState(0);
    const [branches, setBranches] = useState<UserBranches[]>([]);
             
    useEffect(() => {
        getData();
    }, []);

    const getData = async () => {
        const apiUrl = "GetRolesBranches";
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            setRoles(result.data.roles);
            setBranches(result.data.branches);
            setLoading(false);
        };
    }

    const handleRoleChange = (e: any) => {
        setErrors({});

        var newRole: UserRoles = {
            id: parseInt(e.target.value),
            name: e.target.selectedOptions[0].label
        };
        props.addRole(newRole);

        //check if Driver, Truck Team Lead or Truck Labourer added and if so set Driver System to True
        if (newRole.name === "Driver" || newRole.name === "Truck Team Lead" || newRole.name === "Truck Labourer") {
            props.updateDriverSystem(true, false);
        }
    }

    //remove role from selected
    const removeItem = (id: number) => {
        setErrors({});
        props.removeRole(id);
    }

    const handleBranchChange = (e: any) => {
        setErrors({});
        var newBranch: UserBranches = {
            id: parseInt(e.target.value),
            name: e.target.selectedOptions[0].label
        };
        props.addBranch(newBranch);
    }

    const removeBranchItem = (id: number) => {
        setErrors({});
        props.removeBranch(id);
    }

    const handleDriverSytemChange = (e: any) => {
        let skipTruckCheck = props.skipTruckToolCheck;
        if (!e.target.checked) {
            skipTruckCheck = false;
        }

        props.updateDriverSystem(e.target.checked, skipTruckCheck);
    }

    const handleSkipTruckToolCheckChange = (e: any) => {
        props.updateDriverSystem(props.driverSystem, e.target.checked);
    }

    const validate = () => {
        let errorList: any = {};
        let formIsValid = true;

        if (props.selectedUserRoles.length < 1) {
            formIsValid = false;
            errorList["roleId"] = "At least one role is required";
        }

        if (props.selectedUserBranches.length < 1) {
            formIsValid = false;
            errorList["branchId"] = "At least one branch is required";
        }

        setErrors(errorList);
        return formIsValid;
    }

    const save = (e: any) => {
        e.preventDefault();
        if (validate()) {
            props.save();
        }
    }

    //roles
    const availRoles = roles.filter(r => !props.selectedUserRoles.find(sel => sel.id === r.id));

    //branches
    const availBranches = branches.filter(b => !props.selectedUserBranches.find(sel => sel.id === b.id));

    const renderDetails = (
        <div>
            <div className="user__rolebranchsize">
                <div className="user__rolebranchitemsize">
                    <div className="row">
                        <label className="input-group--inline" htmlFor="role">
                            <span className="label">Role</span>
                            <select className="select" id="role" name="roleId" value={roleId} onChange={(e) => handleRoleChange(e)} >
                                <option defaultValue="-1"></option>
                                {availRoles.map(role =>
                                    <option key={role.id} value={role.id}>{role.name}</option>
                                )}
                            </select>
                        </label>
                        <span className="label errors">{errors["roleId"]}</span>
                    </div>
                    <div className="row">
                        <label className="input-group--inline" htmlFor="team">
                            <span className="label"></span>
                            <div className="allocatepopup__wrapItems">
                                {props.selectedUserRoles.map(role =>
                                    <div className="allocatepopup__nowrapItemText input-group" key={role.id} >
                                        <span className="label">{role.name}</span>
                                        <button type="button" className="close makeitred" onClick={() => removeItem(role.id)} >&times;</button>
                                        <br />
                                    </div>
                                )}
                            </div>
                        </label>
                    </div>
                    <label className="input-group" htmlFor="driverSystem">
                        <span className="label">Use Driver System</span>
                        <input className="checkbox" type="checkbox" id="driverSystem" name="driverSystem" checked={props.driverSystem} onChange={(e) => handleDriverSytemChange(e)}></input>
                    </label>
                    <label className={props.driverSystem ? "input-group" : "hidden"} htmlFor="skipTruckToolCheck">
                        <span className="label">Skip Truck/Tool Check</span>
                        <input className="checkbox" type="checkbox" id="skipTruckToolCheck" name="skipTruckToolCheck" checked={props.skipTruckToolCheck} onChange={(e) => handleSkipTruckToolCheckChange(e)}></input>
                    </label>
                </div>
                <div className="user__rolebranchitemsize">
                    <div className="row">
                        <label className="input-group--inline" htmlFor="branch">
                            <span className="label">Branch</span>
                            <select className="select" id="branch" name="branchId" value={branchId} onChange={(e) => handleBranchChange(e)} >
                                <option defaultValue="-1"></option>
                                {availBranches.map(branch =>
                                    <option key={branch.id} value={branch.id}>{branch.name}</option>
                                )}
                            </select>
                        </label>
                        <span className="label errors">{errors["branchId"]}</span>
                    </div>
                    <div className="row">
                        <label className="input-group--inline" htmlFor="team">
                            <span className="label"></span>
                            <div className="allocatepopup__wrapItems">
                                {props.selectedUserBranches.map(branch =>
                                    <div className="allocatepopup__nowrapItemText input-group" key={branch.id} >
                                        <span className="label">{branch.name}</span>
                                        <button type="button" className="close makeitred" onClick={() => removeBranchItem(branch.id)} >&times;</button>
                                        <br />
                                    </div>
                                )}
                            </div>
                        </label>
                    </div>
                </div>
            </div>
            <div className="row">
                <button className="defaultbutton defaultbutton-next" type="button" onClick={save}>Save</button>
            </div>

        </div>
    );

    let contents = loading
        ? <p><em>Loading...</em></p>
        : renderDetails;

    return (<div>
        {contents}
    </div>);

}

export default UserRoleData;